import { ChevronUp, ChevronDown, Trash2, Plus } from "lucide-react";

const swap = (arr, i, d) => { const j = i + d; if (j < 0 || j >= arr.length) return arr; const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a; };

export function RowTools({ list, i, onChange, testid }) {
  return (
    <span className="flex items-center shrink-0">
      <button className="btn-ghost p-1" onClick={() => onChange(swap(list, i, -1))} data-testid={`${testid}-up`}><ChevronUp size={13} /></button>
      <button className="btn-ghost p-1" onClick={() => onChange(swap(list, i, 1))} data-testid={`${testid}-down`}><ChevronDown size={13} /></button>
      <button className="btn-ghost p-1 hover:text-[var(--breach)]" onClick={() => onChange(list.filter((_, k) => k !== i))} data-testid={`${testid}-del`}><Trash2 size={12} /></button>
    </span>
  );
}

export function StagesEditor({ stages, onChange, colors = {}, onColors }) {
  return (
    <div className="space-y-1.5 max-w-md" data-testid="stages-editor">
      {stages.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-neutral-600 w-4">{i + 1}</span>
          <input type="color" className="h-7 w-8 rounded border" value={colors[s] || "#6b7280"} onChange={(e) => onColors({ ...colors, [s]: e.target.value })} data-testid={`stage-color-${i}`} />
          <input className="flex-1 px-2.5 py-1.5 rounded border text-sm" value={s} data-testid={`stage-input-${i}`}
            onChange={(e) => onChange(stages.map((x, k) => (k === i ? e.target.value : x)))} />
          <RowTools list={stages} i={i} onChange={onChange} testid={`stage-${i}`} />
        </div>
      ))}
      <button className="btn" onClick={() => onChange([...stages, "New stage"])} data-testid="add-stage-btn"><Plus size={13} />Add stage</button>
      <div className="text-[10px] text-neutral-600">The first stage is where new leads land. Renaming a stage does not move existing leads.</div>
    </div>
  );
}

const TYPES = ["text", "textarea", "number", "date", "email", "phone", "url", "select"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export function FieldsEditor({ fields, onChange }) {
  const set = (i, p) => onChange(fields.map((f, k) => (k === i ? { ...f, ...p } : f)));
  return (
    <div className="space-y-1.5" data-testid="fields-editor">
      <div className="hidden md:grid grid-cols-[1fr_130px_110px_120px_1fr_50px_70px] gap-2 text-[10px] uppercase tracking-wider text-neutral-500 px-1">
        <span>Label</span><span>Key</span><span>Type</span><span>Section</span><span>Options (select)</span><span>In list</span><span />
      </div>
      {fields.map((f, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-[1fr_130px_110px_120px_1fr_50px_70px] gap-2 items-center panel-2 md:bg-transparent md:border-0 p-2 md:p-0">
          <input className="px-2 py-1 rounded border text-sm" value={f.label} onChange={(e) => set(i, f.builtin ? { label: e.target.value } : { label: e.target.value, key: f._new ? slug(e.target.value) : f.key })} data-testid={`field-label-${i}`} />
          <input className="px-2 py-1 rounded border text-xs font-mono" value={f.key} disabled={f.builtin} onChange={(e) => set(i, { key: slug(e.target.value) })} data-testid={`field-key-${i}`} />
          <select className="px-2 py-1 rounded border text-sm" value={f.type} disabled={f.builtin} onChange={(e) => set(i, { type: e.target.value })} data-testid={`field-type-${i}`}>
            {[...new Set([f.type, ...TYPES])].map((t) => <option key={t}>{t}</option>)}
          </select>
          <input className="px-2 py-1 rounded border text-xs" list="field-sections" value={f.section || ""} placeholder="Details" onChange={(e) => set(i, { section: e.target.value })} data-testid={`field-section-${i}`} />
          <input className="px-2 py-1 rounded border text-xs" disabled={f.type !== "select"} placeholder={f.type === "select" ? "A, B, C" : ""}
            value={(f.options || []).join(", ")} onChange={(e) => set(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid={`field-options-${i}`} />
          <input type="checkbox" checked={!!f.show_in_list} onChange={(e) => set(i, { show_in_list: e.target.checked })} data-testid={`field-inlist-${i}`} />
          {f.builtin ? <span className="text-[10px] text-neutral-600">built-in</span> : <RowTools list={fields} i={i} onChange={onChange} testid={`field-${i}`} />}
        </div>
      ))}
      <datalist id="field-sections">{[...new Set(["Contact", "Deal", "Event", "Attribution", "Details", ...fields.map((f) => f.section).filter(Boolean)])].map((s) => <option key={s} value={s} />)}</datalist>
      <button className="btn" onClick={() => onChange([...fields, { key: `field_${fields.length}`, label: "", type: "text", builtin: false, show_in_list: false, section: "Details", _new: true }])} data-testid="add-field-btn"><Plus size={13} />Add custom field</button>
    </div>
  );
}

export function HooksEditor({ hooks, stages, onChange }) {
  const set = (i, p) => onChange(hooks.map((h, k) => (k === i ? { ...h, ...p } : h)));
  return (
    <div className="space-y-1.5" data-testid="hooks-editor">
      <div className="text-xs text-neutral-500 mb-2">POSTs the lead as JSON to the URL whenever a lead enters the stage — e.g. a Zapier catch hook that triggers WhatsApp or MailerLite flows.</div>
      {hooks.map((h, i) => (
        <div key={i} className="flex flex-col md:flex-row gap-2 md:items-center">
          <select className="px-2 py-1.5 rounded border text-sm md:w-44" value={h.stage || ""} onChange={(e) => set(i, { stage: e.target.value || null })} data-testid={`hook-stage-${i}`}>
            <option value="">Any stage</option>{stages.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input className="flex-1 px-2.5 py-1.5 rounded border text-sm font-mono" placeholder="https://hooks.zapier.com/…" value={h.url} onChange={(e) => set(i, { url: e.target.value })} data-testid={`hook-url-${i}`} />
          <input className="md:w-40 px-2.5 py-1.5 rounded border text-sm" placeholder="Label" value={h.label || ""} onChange={(e) => set(i, { label: e.target.value })} data-testid={`hook-label-${i}`} />
          <RowTools list={hooks} i={i} onChange={onChange} testid={`hook-${i}`} />
        </div>
      ))}
      <button className="btn" onClick={() => onChange([...hooks, { stage: null, url: "", label: "" }])} data-testid="add-hook-btn"><Plus size={13} />Add webhook</button>
    </div>
  );
}
